import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faCircleCheck, faXmark, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { ChangeEvent, useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { HandleSession } from "../function/Login";
import imgDefault from '../assets/imgEditPerfil/user.png';

import CardsFreelancerSkeleton from '../components/Skeleton/cardsFreelancer/cardsFreelancerSkeleton';
import imgListFreeLancer from '../assets/imgs/no_hay_freelances.svg';
import ModalLogin from '../components/modals/modalLogin';
import ImageWithPreloader from "../components/ImageWithPreloader/ImageWithPreloader";

// Mejoras Chambaticon §A.3/§B.3: buscador extendido para empresas, sobre el mismo
// `freelancer/search-advanced` del backend (§A.4) — endpoint aparte de `freelancer/search`,
// que sigue existiendo intacto (§3.2 del plan, no se toca ni se borra).

interface Competency {
    name: string;
    level_tier: number | null;
    level_tier_label: string | null;
    validated: boolean;
    competency_type: number | null;
}

interface FreelancerResult {
    id: number;
    names: string;
    lastnames: string;
    username: string;
    state: string;
    about_me: string | null;
    photo: string | null;
    validated_count: number;
    max_level_tier: number | null;
    competencies: Competency[];
    job_avaible: number | null;
    employment_type_interest: number[];
}

interface CompetencySuggestion {
    id: number;
    name: string;
    competency_type: number | null;
}

interface CatalogOption {
    id: number;
    name: string;
}

interface Pagination {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

type MatchMode = 'any' | 'all';
type OrderBy = 'recent' | 'level' | 'validated';

const LEVEL_TIER_LABELS: Record<string, string> = { '1': 'Básico', '2': 'Intermedio', '3': 'Avanzado', '4': 'Experto' };

const ListFreelance = () => {
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    // Filtros — inicializados desde la URL para que una búsqueda se pueda compartir (§B.3).
    const [q, setQ] = useState(searchParams.get('q') || '');
    const [qDraft, setQDraft] = useState(searchParams.get('q') || '');
    const [selectedCompetencies, setSelectedCompetencies] = useState<CompetencySuggestion[]>(
        (searchParams.get('competency_names') || '').split('|').filter(Boolean).map((name, index) => ({
            id: Number((searchParams.get('competencies') || '').split(',').filter(Boolean)[index]) || 0,
            name,
            competency_type: null,
        }))
    );
    const [matchMode, setMatchMode] = useState<MatchMode>((searchParams.get('match_mode') as MatchMode) || 'any');
    const [competencyType, setCompetencyType] = useState(searchParams.get('competency_type') || '');
    const [minLevelTier, setMinLevelTier] = useState(searchParams.get('min_level_tier') || '');
    const [onlyValidated, setOnlyValidated] = useState(searchParams.get('only_validated') === 'true');
    const [stateId, setStateId] = useState(searchParams.get('state_id') || '');
    const [specializationTypeId, setSpecializationTypeId] = useState(searchParams.get('specialization_type_id') || '');
    const [orderBy, setOrderBy] = useState<OrderBy>((searchParams.get('order_by') as OrderBy) || 'recent');
    const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
    // Solicitud del cliente: disponibilidad (modalidad, no booleano) y tipo(s) de empleo de
    // interés del beneficiario — ambos multiselección de checkboxes sobre la misma idea.
    const [jobAvailableIds, setJobAvailableIds] = useState<string[]>(
        (searchParams.get('job_avaible') || '').split(',').filter(Boolean)
    );
    const [employmentTypeIds, setEmploymentTypeIds] = useState<string[]>(
        (searchParams.get('employment_type_interest') || '').split(',').filter(Boolean)
    );

    // Catálogos para los selects (áreas temáticas y departamentos).
    const [specializationTypes, setSpecializationTypes] = useState<CatalogOption[]>([]);
    const [states, setStates] = useState<CatalogOption[]>([]);
    const [employmentTypeOptions, setEmploymentTypeOptions] = useState<CatalogOption[]>([]);
    const [availabilityTypes, setAvailabilityTypes] = useState<CatalogOption[]>([]);

    // Autocompletado de competencias.
    const [competencyQuery, setCompetencyQuery] = useState('');
    const [competencySuggestions, setCompetencySuggestions] = useState<CompetencySuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [results, setResults] = useState<FreelancerResult[]>([]);
    const [pagination, setPagination] = useState<Pagination>({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    const verifySession = HandleSession();
    const [addClassModal, setAddClassModal] = useState(false);
    const [IsLoadingModal, setIsLoadingModal] = useState(false);
    const urlEnlacePerfil = `/profile-public/`;

    // Catálogos de área temática y departamento — una sola vez.
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URI}Knowledge-specialization/list/types`)
            .then((resp) => setSpecializationTypes(resp.data.specializationTypes || []))
            .catch((err) => console.error(err));

        axios.get(`${process.env.REACT_APP_API_URI}state/3`) // El Salvador
            .then((resp) => setStates(resp.data || []))
            .catch((err) => console.error(err));

        axios.get(`${process.env.REACT_APP_API_URI}freelancer/list/work-type-interests`)
            .then((resp) => setEmploymentTypeOptions(resp.data.data || []))
            .catch((err) => console.error(err));

        axios.get(`${process.env.REACT_APP_API_URI}freelancer/list/availability-types`)
            .then((resp) => setAvailabilityTypes(resp.data.data || []))
            .catch((err) => console.error(err));
    }, []);

    // Debounce del texto libre.
    useEffect(() => {
        const timeout = setTimeout(() => {
            setQ(qDraft.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timeout);
    }, [qDraft]);

    // Autocompletado de competencias, con debounce.
    useEffect(() => {
        if (competencyQuery.trim().length < 2) {
            setCompetencySuggestions([]);
            return;
        }

        const timeout = setTimeout(() => {
            axios.get(`${process.env.REACT_APP_API_URI}competencies/search?q=${encodeURIComponent(competencyQuery.trim())}`)
                .then((resp) => setCompetencySuggestions(resp.data.data || []))
                .catch((err) => console.error(err));
        }, 300);

        return () => clearTimeout(timeout);
    }, [competencyQuery]);

    const addCompetency = (competency: CompetencySuggestion) => {
        if (!selectedCompetencies.some((c) => c.id === competency.id)) {
            setSelectedCompetencies((prev) => [...prev, competency]);
            setPage(1);
        }
        setCompetencyQuery('');
        setShowSuggestions(false);
    };

    const removeCompetency = (id: number) => {
        setSelectedCompetencies((prev) => prev.filter((c) => c.id !== id));
        setPage(1);
    };

    const toggleEmploymentType = (id: number) => {
        const idStr = String(id);
        setEmploymentTypeIds((prev) => prev.includes(idStr) ? prev.filter((v) => v !== idStr) : [...prev, idStr]);
        setPage(1);
    };

    const toggleJobAvailable = (id: number) => {
        const idStr = String(id);
        setJobAvailableIds((prev) => prev.includes(idStr) ? prev.filter((v) => v !== idStr) : [...prev, idStr]);
        setPage(1);
    };

    const clearFilters = () => {
        setQ(''); setQDraft('');
        setSelectedCompetencies([]);
        setMatchMode('any');
        setCompetencyType('');
        setMinLevelTier('');
        setOnlyValidated(false);
        setStateId('');
        setSpecializationTypeId('');
        setJobAvailableIds([]);
        setEmploymentTypeIds([]);
        setOrderBy('recent');
        setPage(1);
    };

    // Trae resultados y sincroniza la URL — un talento de prueba puede compartir el enlace
    // y reproducir la misma búsqueda al abrirlo (§B.3).
    const fetchResults = () => {
        setIsLoading(true);
        setErrored(false);

        const queryParams = new URLSearchParams();
        if (q) queryParams.set('q', q);
        if (selectedCompetencies.length > 0) {
            queryParams.set('competencies', selectedCompetencies.map((c) => c.id).join(','));
            queryParams.set('competency_names', selectedCompetencies.map((c) => c.name).join('|'));
        }
        if (selectedCompetencies.length > 1) queryParams.set('match_mode', matchMode);
        if (competencyType) queryParams.set('competency_type', competencyType);
        if (minLevelTier) queryParams.set('min_level_tier', minLevelTier);
        if (onlyValidated) queryParams.set('only_validated', 'true');
        if (stateId) queryParams.set('state_id', stateId);
        if (specializationTypeId) queryParams.set('specialization_type_id', specializationTypeId);
        if (jobAvailableIds.length > 0) queryParams.set('job_avaible', jobAvailableIds.join(','));
        if (employmentTypeIds.length > 0) queryParams.set('employment_type_interest', employmentTypeIds.join(','));
        if (orderBy !== 'recent') queryParams.set('order_by', orderBy);
        if (page > 1) queryParams.set('page', String(page));
        if (params.id) queryParams.set('specialization_type_id', params.id);

        setSearchParams(queryParams, { replace: true });

        const apiParams = new URLSearchParams(queryParams);
        apiParams.delete('competency_names'); // solo para reproducir la URL, no lo pide el backend

        axios.get(`${process.env.REACT_APP_API_URI}freelancer/search-advanced?${apiParams.toString()}`)
            .then((resp) => {
                setResults(resp.data.data || []);
                setPagination({
                    current_page: resp.data.current_page,
                    last_page: resp.data.last_page,
                    per_page: resp.data.per_page,
                    total: resp.data.total,
                });
            })
            .catch((err) => {
                console.error(err);
                setResults([]);
                setErrored(true);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, selectedCompetencies, matchMode, competencyType, minLevelTier, onlyValidated, stateId, specializationTypeId, jobAvailableIds, employmentTypeIds, orderBy, page]);

    const activeFilterChips: { label: string; onRemove: () => void }[] = [
        ...(q ? [{ label: `"${q}"`, onRemove: () => { setQ(''); setQDraft(''); } }] : []),
        ...selectedCompetencies.map((c) => ({ label: c.name, onRemove: () => removeCompetency(c.id) })),
        ...(competencyType ? [{ label: competencyType === '1' ? 'Técnicas' : 'Blandas', onRemove: () => setCompetencyType('') }] : []),
        ...(minLevelTier ? [{ label: `Nivel mínimo: ${LEVEL_TIER_LABELS[minLevelTier]}`, onRemove: () => setMinLevelTier('') }] : []),
        ...(onlyValidated ? [{ label: 'Solo validadas', onRemove: () => setOnlyValidated(false) }] : []),
        ...(stateId ? [{ label: states.find((s) => String(s.id) === stateId)?.name || 'Departamento', onRemove: () => setStateId('') }] : []),
        ...(specializationTypeId ? [{ label: specializationTypes.find((s) => String(s.id) === specializationTypeId)?.name || 'Área', onRemove: () => setSpecializationTypeId('') }] : []),
        ...jobAvailableIds.map((id) => ({
            label: availabilityTypes.find((t) => String(t.id) === id)?.name || 'Disponibilidad',
            onRemove: () => toggleJobAvailable(Number(id)),
        })),
        ...employmentTypeIds.map((id) => ({
            label: employmentTypeOptions.find((t) => String(t.id) === id)?.name || 'Tipo de empleo',
            onRemove: () => toggleEmploymentType(Number(id)),
        })),
    ];

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.last_page && newPage !== page) {
            setPage(newPage);
        }
    };

    const upModal = (state: Boolean) => {
        if (state) {
            setAddClassModal(true);
        } else {
            setAddClassModal(false);
        }
        setIsLoadingModal(true);
        setInterval(() => {
            setIsLoadingModal(false);
        }, 1000);
    }

    const renderPagination = () => {
        if (pagination.last_page <= 1) return null;

        return (
            <div className="flex justify-center mt-6">
                <nav className="inline-flex rounded-md shadow">
                    <button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1 || isLoading}
                        className={`px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${pagination.current_page === 1 || isLoading ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>

                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((p, index) => (
                        <button
                            key={index}
                            onClick={() => handlePageChange(p)}
                            disabled={isLoading || p === pagination.current_page}
                            className={`px-3 py-2 border-t border-b border-gray-300 text-sm font-medium ${p !== pagination.current_page ? 'bg-white' : ''} ${p === pagination.current_page ? 'bg-Yellow text-white' : isLoading ? 'text-gray-400' : 'text-gray-700 hover:bg-gray-50'}`}
                        >
                            {p}
                        </button>
                    ))}

                    <button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page || isLoading}
                        className={`px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${pagination.current_page === pagination.last_page || isLoading ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </nav>
            </div>
        );
    };

    return (
        <>
            <div className="bg-gradient-to-b from-Light-purple via-turquoise to-Dark-purple">
                <section className="block -mt-px md:flex md:flex-wrap">
                    <div className="md:w-full lg:w-full flex justify-center pb-5 lg:py-5">
                        <div className="mx-1 lg:mx-10 w-full">
                            <div className="flex justify-center pb-2 lg:pb-4 mt-4 lg:mt-0">
                                <h1 className="font-bold text-lg sm:text-2xl roboto text-Yellow">Encuentra al talento ideal para tu proyecto</h1>
                            </div>

                            <div className="flex flex-wrap lg:flex-nowrap gap-4 px-2 lg:px-6">
                                {/* Panel de filtros (§B.3) */}
                                <aside className="w-full lg:w-1/4 bg-[#090F2C] border border-gray-700 rounded-lg p-4 h-fit">
                                    <div className="mb-4">
                                        <label className="text-white text-sm font-medium mb-1 block">Buscar</label>
                                        <div className="relative">
                                            <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-3 text-gray-400" />
                                            <input
                                                type="text"
                                                className="bg-color-input-login w-full pl-9 pr-3 py-2 text-sm text-white rounded border border-gray-600 focus:outline-none focus:border-Yellow"
                                                placeholder="Nombre o descripción..."
                                                value={qDraft}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => setQDraft(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4 relative">
                                        <label className="text-white text-sm font-medium mb-1 block">Competencia</label>
                                        <input
                                            type="text"
                                            className="bg-color-input-login w-full px-3 py-2 text-sm text-white rounded border border-gray-600 focus:outline-none focus:border-Yellow"
                                            placeholder="Ej. JavaScript, Trabajo en equipo..."
                                            value={competencyQuery}
                                            onChange={(e) => { setCompetencyQuery(e.target.value); setShowSuggestions(true); }}
                                            onFocus={() => setShowSuggestions(true)}
                                        />
                                        {showSuggestions && competencySuggestions.length > 0 && (
                                            <ul className="absolute z-10 w-full bg-white rounded shadow-lg mt-1 max-h-48 overflow-y-auto">
                                                {competencySuggestions.map((s) => (
                                                    <li
                                                        key={s.id}
                                                        onClick={() => addCompetency(s)}
                                                        className="px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        {s.name}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {selectedCompetencies.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {selectedCompetencies.map((c) => (
                                                    <span key={c.id} className="bg-Yellow text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                                        {c.name}
                                                        <FontAwesomeIcon icon={faXmark} className="cursor-pointer" onClick={() => removeCompetency(c.id)} />
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {selectedCompetencies.length > 1 && (
                                            <div className="flex gap-2 mt-2 text-xs">
                                                <label className="flex items-center gap-1 text-gray-300">
                                                    <input type="radio" checked={matchMode === 'any'} onChange={() => setMatchMode('any')} /> Alguna
                                                </label>
                                                <label className="flex items-center gap-1 text-gray-300">
                                                    <input type="radio" checked={matchMode === 'all'} onChange={() => setMatchMode('all')} /> Todas
                                                </label>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-4">
                                        <label className="text-white text-sm font-medium mb-1 block">Tipo</label>
                                        <select
                                            className="bg-color-input-login w-full px-3 py-2 text-sm text-white rounded border border-gray-600"
                                            value={competencyType}
                                            onChange={(e) => setCompetencyType(e.target.value)}
                                        >
                                            <option value="">Todas</option>
                                            <option value="1">Técnicas</option>
                                            <option value="2">Blandas</option>
                                        </select>
                                    </div>

                                    <div className="mb-4">
                                        <label className="text-white text-sm font-medium mb-1 block">Nivel mínimo</label>
                                        <select
                                            className="bg-color-input-login w-full px-3 py-2 text-sm text-white rounded border border-gray-600"
                                            value={minLevelTier}
                                            onChange={(e) => setMinLevelTier(e.target.value)}
                                        >
                                            <option value="">Cualquiera</option>
                                            <option value="1">Básico</option>
                                            <option value="2">Intermedio</option>
                                            <option value="3">Avanzado</option>
                                            <option value="4">Experto</option>
                                        </select>
                                    </div>

                                    <div className="mb-4 bg-blue-950 border border-blue-500 rounded-lg p-3">
                                        <label className="flex items-center gap-2 text-white text-sm font-semibold cursor-pointer">
                                            <input type="checkbox" checked={onlyValidated} onChange={(e) => setOnlyValidated(e.target.checked)} />
                                            <FontAwesomeIcon icon={faCircleCheck} className="text-Yellow" />
                                            Solo competencias validadas
                                        </label>
                                        <p className="text-xs text-gray-300 mt-1">
                                            Talento cuyas competencias fueron evaluadas por CONEXIÓN.
                                        </p>
                                    </div>

                                    <div className="mb-4">
                                        <label className="text-white text-sm font-medium mb-1 block">Departamento</label>
                                        <select
                                            className="bg-color-input-login w-full px-3 py-2 text-sm text-white rounded border border-gray-600"
                                            value={stateId}
                                            onChange={(e) => setStateId(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>

                                    <div className="mb-4">
                                        <label className="text-white text-sm font-medium mb-1 block">Área temática</label>
                                        <select
                                            className="bg-color-input-login w-full px-3 py-2 text-sm text-white rounded border border-gray-600"
                                            value={specializationTypeId}
                                            onChange={(e) => setSpecializationTypeId(e.target.value)}
                                        >
                                            <option value="">Todas</option>
                                            {specializationTypes.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>

                                    {availabilityTypes.length > 0 && (
                                        <div className="mb-4">
                                            <label className="text-white text-sm font-medium mb-1 block">Disponibilidad</label>
                                            <div className="flex flex-col gap-1">
                                                {/* Solicitud del cliente: "No disponible" no se ofrece como filtro — el
                                                    buscador ya excluye de base a quien no está disponible, así que
                                                    filtrar por ese valor nunca encontraría a nadie. */}
                                                {availabilityTypes.filter((type) => type.id !== 0).map((type) => (
                                                    <label key={type.id} className="flex items-center gap-2 text-gray-300 text-xs cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={jobAvailableIds.includes(String(type.id))}
                                                            onChange={() => toggleJobAvailable(type.id)}
                                                        />
                                                        {type.name}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {employmentTypeOptions.length > 0 && (
                                        <div className="mb-4">
                                            <label className="text-white text-sm font-medium mb-1 block">Tipo de empleo de interés</label>
                                            <div className="flex flex-col gap-1">
                                                {/* Solicitud del cliente: "Trabajo de tiempo completo" y "Trabajo de medio
                                                    tiempo" no se ofrecen en este filtro — se solapan con el filtro de
                                                    Disponibilidad (Tiempo completo/Medio tiempo). Siguen disponibles como
                                                    opción para el talento al elegir su tipo de empleo de interés. */}
                                                {employmentTypeOptions
                                                    .filter((type) => type.id !== 4 && type.id !== 5)
                                                    .map((type) => (
                                                    <label key={type.id} className="flex items-center gap-2 text-gray-300 text-xs cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={employmentTypeIds.includes(String(type.id))}
                                                            onChange={() => toggleEmploymentType(type.id)}
                                                        />
                                                        {type.name}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-2">
                                        <label className="text-white text-sm font-medium mb-1 block">Ordenar por</label>
                                        <select
                                            className="bg-color-input-login w-full px-3 py-2 text-sm text-white rounded border border-gray-600"
                                            value={orderBy}
                                            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
                                        >
                                            <option value="recent">Más recientes</option>
                                            <option value="level">Mayor nivel</option>
                                            <option value="validated">Más competencias validadas</option>
                                        </select>
                                    </div>

                                    {activeFilterChips.length > 0 && (
                                        <button
                                            onClick={clearFilters}
                                            className="w-full mt-2 text-xs text-Yellow underline"
                                        >
                                            Limpiar filtros
                                        </button>
                                    )}
                                </aside>

                                {/* Resultados */}
                                <div className="w-full lg:w-3/4">
                                    {activeFilterChips.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {activeFilterChips.map((chip, index) => (
                                                <span key={index} className="bg-white/10 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                                    {chip.label}
                                                    <FontAwesomeIcon icon={faXmark} className="cursor-pointer" onClick={chip.onRemove} />
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <p className="text-gray-300 text-sm mb-3">
                                        {isLoading ? 'Buscando...' : `${pagination.total} talento${pagination.total === 1 ? '' : 's'} encontrado${pagination.total === 1 ? '' : 's'}`}
                                    </p>

                                    {errored && (
                                        <p className="text-center text-white text-sm mt-6">No se pudo completar la búsqueda. Intenta de nuevo.</p>
                                    )}

                                    {!errored && isLoading && <CardsFreelancerSkeleton cards={10} />}

                                    {!errored && !isLoading && results.length === 0 && (
                                        <div>
                                            <p className="text-center text-white text-xl mt-6">No se encontraron talentos con estos filtros</p>
                                            <p className="text-center text-gray-400 text-sm mt-2">
                                                Prueba quitando el nivel mínimo, desactivando "solo validadas" o eligiendo menos competencias.
                                            </p>
                                            <div className="flex justify-center">
                                                <img src={imgListFreeLancer} alt="" width="40%" />
                                            </div>
                                        </div>
                                    )}

                                    {!errored && !isLoading && results.length > 0 && (
                                        <div className="flex flex-wrap">
                                            {results.map((freelancer) => (
                                                <div key={freelancer.id} className="p-2 lg:w-1/2 w-full">
                                                    <div className="h-full flex items-start bg-[#090F2C] border-gray-200 border p-3 rounded-lg">
                                                        <ImageWithPreloader src={freelancer.photo || imgDefault} alt="team" />
                                                        <div className="flex-grow">
                                                            <div className="flex items-center gap-2">
                                                                <h2 className="text-white title-font font-medium text-sm">{freelancer.names} {freelancer.lastnames}</h2>
                                                                {freelancer.validated_count > 0 && (
                                                                    <span className="bg-Yellow text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                                                                        <FontAwesomeIcon icon={faCircleCheck} /> Validado
                                                                    </span>
                                                                )}
                                                                {!!freelancer.job_avaible && (
                                                                    <span className="bg-green-600 text-white text-[10px] px-2 py-0.5 rounded-full">
                                                                        {availabilityTypes.find((t) => t.id === freelancer.job_avaible)?.name || 'Disponible'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-gray-400 text-xs">{freelancer.state}</p>

                                                            <div className="flex flex-wrap gap-1 mt-2">
                                                                {freelancer.competencies.slice(0, 4).map((c, index) => (
                                                                    <span
                                                                        key={index}
                                                                        className={`text-2xs px-2 py-1 rounded flex items-center gap-1 ${c.validated ? 'bg-Yellow text-white' : 'bg-white/10 text-gray-200'}`}
                                                                    >
                                                                        {c.validated && <FontAwesomeIcon icon={faCircleCheck} className="text-[9px]" />}
                                                                        {c.name}{c.level_tier_label ? ` · ${c.level_tier_label}` : ''}
                                                                    </span>
                                                                ))}
                                                                {freelancer.competencies.length > 4 && (
                                                                    <span className="text-2xs text-gray-400">+{freelancer.competencies.length - 4} más</span>
                                                                )}
                                                            </div>

                                                            <p className="text-white text-xs text-justify mt-2">
                                                                {freelancer.about_me?.substring(0, 100)} {(freelancer.about_me?.length ?? 0) > 100 ? '...' : ''}
                                                            </p>

                                                            <div className="pt-2 flex justify-end">
                                                                {(verifySession.Session == false) ? (
                                                                    <button
                                                                        className="bg-Yellow w-full sm:w-1/2 lg:w-1/3 text-center text-white font-bold rounded px-2 py-1.5 mt-1"
                                                                        onClick={() => upModal(true)}
                                                                    >
                                                                        Ver perfil
                                                                    </button>
                                                                ) : (
                                                                    <Link to={`${urlEnlacePerfil}${freelancer.username}/${freelancer.id}`}
                                                                        className="bg-Yellow w-full sm:w-1/2 lg:w-1/3 text-center text-white rounded px-2 py-1.5 mt-1 text-xs">
                                                                        Ver perfil
                                                                    </Link>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {renderPagination()}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <ModalLogin addClassModal={addClassModal} setAddClassModal={setAddClassModal} IsLoadingModal={IsLoadingModal} />
        </>
    )
}

export default ListFreelance;
